import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  BadgeCheck,
  BookOpenCheck,
  CalendarDays,
  CheckCircle2,
  Loader2,
  ReceiptText,
  RefreshCw,
} from 'lucide-react';
import { summativeExamService } from '../../../services/summativeExamService';

const money = value => `KES ${Number(value || 0).toLocaleString('en-KE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const typeTone = {
  core: 'bg-blue-50 text-blue-700 border-blue-200',
  basic: 'bg-amber-50 text-amber-700 border-amber-200',
  common: 'bg-violet-50 text-violet-700 border-violet-200',
};

const formatBookingDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-KE', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const resolveBookingControl = settings => {
  if (settings && Object.prototype.hasOwnProperty.call(settings, 'can_book')) {
    const enabled = settings.booking_enabled !== false;
    const opensAt = settings.opens_at || null;
    const closesAt = settings.closes_at || null;
    const state = settings.status === 'not_open' ? 'not_open' : settings.status === 'disabled' || settings.status === 'closed' ? 'closed' : 'open';
    const message = state === 'not_open'
      ? `Booking opens on ${formatBookingDate(opensAt)}.`
      : state === 'closed'
        ? (settings.status === 'disabled' ? 'Summative examination booking is currently closed for this academic period.' : `Booking closed on ${formatBookingDate(closesAt)}.`)
        : closesAt ? `Booking closes on ${formatBookingDate(closesAt)}.` : 'Continuous booking is enabled for this academic period.';
    return { enabled, opensAt, closesAt, closedAt: closesAt, canBookNow: settings.can_book !== false, state, message };
  }
  const enabled = settings?.summative_exam_booking_enabled !== false;
  const opensAt = settings?.summative_exam_booking_opens_at || null;
  const closesAt = settings?.summative_exam_booking_closes_at || null;
  const now = Date.now();
  const opensInFuture = opensAt && new Date(opensAt).getTime() > now;
  const closesInPast = closesAt && new Date(closesAt).getTime() <= now;
  let message = 'Continuous booking is enabled.';
  let state = 'open';
  if (!enabled) {
    state = 'closed';
    message = 'Summative examination booking is currently closed.';
  } else if (opensInFuture) {
    state = 'not_open';
    message = `Booking opens on ${formatBookingDate(opensAt)}.`;
  } else if (closesInPast) {
    state = 'closed';
    message = 'The summative examination booking window has closed.';
  } else if (closesAt) {
    message = `Booking closes on ${formatBookingDate(closesAt)}.`;
  }
  const closedAt = closesAt || (!opensInFuture && opensAt ? opensAt : null);
  return { enabled, opensAt, closesAt, closedAt, canBookNow: enabled && state !== 'not_open' && state !== 'closed', state, message };
};

const StudentSummativeExamDashboard = () => {
  const [dashboard, setDashboard] = React.useState({ student: null, period: null, units: [], booking_control: resolveBookingControl(null) });
  const [loading, setLoading] = React.useState(true);
  const [bookingId, setBookingId] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [confirmModal, setConfirmModal] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setNotice(null);
    try {
      const studentDashboard = await summativeExamService.getStudentDashboard();
      setDashboard({ ...studentDashboard, booking_control: resolveBookingControl(studentDashboard.booking_window) });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const confirmBooking = unit => {
    const charge = money(unit.exam_fee);
    setConfirmModal({
      unit,
      message: `Book the summative examination for ${unit.unit_code}? ${charge} will be added to your fee statement.`
    });
  };

  const executeBooking = async () => {
    if (!confirmModal) return;
    const unit = confirmModal.unit;
    setConfirmModal(null);
    setBookingId(unit.unit_registration_id);
    setNotice(null);
    try {
      const result = await summativeExamService.bookUnit(unit.unit_registration_id);
      await load();
      setNotice({ type: 'success', text: `${result.unit_code} booked successfully. ${money(result.fee_amount)} has been added to your fee statement.` });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally {
      setBookingId(null);
    }
  };

  const units = dashboard.units || [];
  const booked = units.filter(unit => unit.booking_id && unit.booking_status === 'booked');
  const bookedTotal = booked.reduce((sum, unit) => sum + Number(unit.billed_amount || 0), 0);
  const period = dashboard.period;
  const bookingControl = dashboard.booking_control || resolveBookingControl(null);

  return (
    <div className="space-y-4">
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-bold text-slate-950">Summative examination booking</h1>
            {period && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{period.academic_year_name} · {period.semester_name}</span>}
          </div>
          <p className="mt-1 text-xs text-slate-500">Book each registered unit. Its configured examination fee is billed immediately. A maximum of 10 examinations applies per semester.</p>
        </div>
        <div className="flex items-center gap-2"><Link to="/summative-assessment-portal?view=history" className="inline-flex h-9 items-center gap-2 rounded-lg border border-blue-200 px-3 text-xs font-semibold text-blue-700 hover:bg-blue-50">Historical progress</Link><button type="button" onClick={load} disabled={loading} className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button></div>
      </section>

      {notice && (
        <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${notice.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
          {notice.type === 'success' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{notice.text}</span>
        </div>
      )}

      <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-3 text-sm ${bookingControl.canBookNow ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
        <div>
          <p className="font-semibold">Booking availability</p>
          <p className="mt-0.5 text-xs">{bookingControl.message}</p>
        </div>
        {bookingControl.state === 'not_open' && bookingControl.opensAt && <span className="text-xs font-medium">Opens: {formatBookingDate(bookingControl.opensAt)}</span>}
        {bookingControl.state === 'closed' && bookingControl.closedAt && <span className="text-xs font-medium">Closed: {formatBookingDate(bookingControl.closedAt)}</span>}
        {bookingControl.state === 'open' && bookingControl.closesAt && <span className="text-xs font-medium">Closes: {formatBookingDate(bookingControl.closesAt)}</span>}
      </div>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ['Registered units', units.length, BookOpenCheck, 'bg-blue-50 text-blue-700'],
          ['Exams booked', booked.length, BadgeCheck, 'bg-emerald-50 text-emerald-700'],
          ['Awaiting booking', Math.max(units.length - booked.length, 0), CalendarDays, 'bg-amber-50 text-amber-700'],
          ['Exam fees billed', money(bookedTotal), ReceiptText, 'bg-violet-50 text-violet-700'],
        ].map(([label, value, Icon, tone]) => (
          <div key={label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></span>
            <div><p className="text-xs font-medium text-slate-500">{label}</p><p className="mt-0.5 text-lg font-bold text-slate-950">{value}</p></div>
          </div>
        ))}
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="text-sm font-bold text-slate-950">My registered units</h2>
            <p className="text-xs text-slate-500">Core, Basic and Common fees come from System Settings.</p>
          </div>
          <Link to="/student-finance/fees-statement" className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-slate-950 px-3 text-xs font-semibold text-white hover:bg-slate-800">
            <ReceiptText className="h-3.5 w-3.5" /> View fee statement
          </Link>
        </div>

        {loading ? (
          <div className="grid min-h-48 place-items-center text-sm text-slate-500"><span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading examination units…</span></div>
        ) : !period ? (
          <div className="p-10 text-center"><AlertCircle className="mx-auto h-8 w-8 text-amber-500" /><h3 className="mt-2 font-semibold text-slate-900">No current semester registration</h3><p className="mt-1 text-sm text-slate-500">Complete semester registration before booking summative examinations.</p></div>
        ) : units.length === 0 ? (
          <div className="p-10 text-center"><BookOpenCheck className="mx-auto h-8 w-8 text-slate-300" /><h3 className="mt-2 font-semibold text-slate-900">No registered units found</h3><p className="mt-1 text-sm text-slate-500">There are no enrolled units in {period.semester_name}.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">Unit</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Exam fee</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {units.map(unit => {
                  const isBooked = unit.booking_id && unit.booking_status === 'booked';
                  const configured = unit.exam_fee !== null && unit.exam_fee !== undefined;
                  const canBook = period.can_book && bookingControl.canBookNow && configured && !isBooked;
                  const bookingUnavailableLabel = !bookingControl.enabled || (bookingControl.closesAt && new Date(bookingControl.closesAt).getTime() <= Date.now())
                    ? 'Booking closed'
                    : bookingControl.opensAt && new Date(bookingControl.opensAt).getTime() > Date.now()
                      ? 'Not yet open'
                      : null;
                  return (
                    <tr key={unit.unit_registration_id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3"><p className="font-bold text-slate-900">{unit.unit_code}</p><p className="mt-0.5 text-xs text-slate-500">{unit.unit_name}</p></td>
                      <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${typeTone[unit.unit_type] || typeTone.core}`}>{unit.unit_type}</span></td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{configured ? money(unit.exam_fee) : <span className="font-sans text-xs text-rose-600">Not configured</span>}</td>
                      <td className="px-4 py-3">{isBooked ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Booked & billed</span> : <span className="text-xs font-medium text-slate-500">Not booked</span>}</td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" disabled={!canBook || bookingId === unit.unit_registration_id} onClick={() => confirmBooking(unit)} className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-700 px-3 text-xs font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500">
                          {bookingId === unit.unit_registration_id && <Loader2 className="h-4 w-4 animate-spin" />}
                          {isBooked ? 'Booked' : !period.can_book ? 'Complete registration' : bookingUnavailableLabel || (!configured ? 'Fee not set' : 'Book exam')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50">
                <AlertCircle className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Confirm booking</h3>
            </div>
            <p className="mt-4 text-sm text-slate-600">
              {confirmModal.message}
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmModal(null)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeBooking}
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 transition-colors shadow-sm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentSummativeExamDashboard;
